<script setup lang="ts">
import { Button, PortalIcon, RelativeDateTime, useTranslation, Loader, I18n, Icon, diffFromNow, Tooltip } from '@bluebrick/saas-ui'
import { capitalize } from 'lodash-es'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { watch, computed, type Ref, toRefs, onMounted } from 'vue'
import { StoreToPortalMap, portalFunctionalities, sortByHumanNameComparator, StoreToPortalFunctionality, CSVDownload } from '@/common/utils'
import { useAppStore } from '@/features/app/useAppStore'
import { ScraperConfigurationStatus } from '@/features/dataHealth/types'
import { useDataHealthStore } from '@/features/dataHealth/useDataHealthStore'
import { useEventDetailsStore } from '@/features/eventDetails/useEventDetailsStore'
import StoreEventsTimeline from '@/features/events/components/timeline/StoreEventsTimeline.vue'
import { useEventsStore } from '@/features/events/useEventsStore'
import { emitToParent } from '@/features/iframe/useParentEmit'
import { usePreferencesStore } from '@/features/preferences/usePreferencesStore'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { useCalendarStore } from '@/features/slicers/useCalendarStore'
import NoStoresPlaceholder from '@/features/stores/components/NoStoresPlaceholder.vue'
import PortalFunctionality from '@/features/stores/components/PortalFunctionality.vue'
import StoreDatesNavigation from '@/features/stores/components/StoreDatesNavigation.vue'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { useFeatureFlagStore } from '@/features/user/useFeatureFlagStore'
import T from '@/translations/T.vue'
import { SidebarContent, type Store } from '@/types'

const props = defineProps<{
	store: Store
	isInitializing: boolean
}>()

const emit = defineEmits(['reload'])

const { store } = toRefs(props)

const portal = StoreToPortalMap.get(props.store.id)!

const portalCapitalized = capitalize(portal.toLocaleLowerCase())

const { products } = storeToRefs(useEventDetailsStore())
const { createEvent } = useEventDetailsStore()
const { dataRangeStartDate, dataRangeEndDate } = storeToRefs(useCalendarStore())
const { trackEvent } = useTelemetry()
const { portalsStatusMap, discountsLastScrapeDates } = storeToRefs(useDataHealthStore())
const { storeSKUs, storeDataLoadingState, isLoadingEventsWithCooldowns, isLoadingStoreData } = storeToRefs(useEventsStore())
const { loadStoreData } = useEventsStore()
const { isSidebarVisible } = storeToRefs(useSidebarStore())
const { showFavouriteSKUs } = useSidebarStore()
const { hasScrapersEnabled } = storeToRefs(useFeatureFlagStore())
const { isEmbedded } = storeToRefs(useAppStore())
const { favouriteSKUIdsByStore, favouriteSKUIds } = storeToRefs(useSkusStore())
const { toggleStoreContainerExpandedState } = useStoresStore()
const { storeContainerExpandedState } = storeToRefs(useStoresStore())
const { isUsingStoreSpecificTimeZones } = storeToRefs(usePreferencesStore())

const { t } = useTranslation()

const isContentVisible = computed(() => !!storeContainerExpandedState.value.get(store.value.id))

const isEventSidebarVisible = computed(() => isSidebarVisible.value(SidebarContent.EVENT_SETUP))

const setContentVisibility = (visibility: boolean) => {
	trackEvent('user', 'Toggled store container.', { store: props.store.displayName, isOpen: visibility })

	toggleStoreContainerExpandedState(store.value.id, visibility)
}

// Indicates that studio has some favourite SKUs for other store(s), but does not have favourite SKUs
// for this particular store
const storeHasNoFavourites = computed(() => favouriteSKUIds.value.length > 0 && !favouriteSKUIdsByStore.value.get(store.value.id)?.length)

const fetchData = async () => {
	await loadStoreData(props.store.id)
}

onMounted(fetchData)

// Loads the data on page load
watch(() => props.store.id, fetchData)

// Loads the data if dates range changes and if the collapsible store panel is open
watch([dataRangeStartDate, dataRangeEndDate, isContentVisible], fetchData)

const addEventClicked = () => {
	trackEvent('user', 'Clicked add event button.', { store: props.store.displayName })

	createEvent(props.store)
}

const hasError: Ref<boolean> = computed(() => portalsStatusMap.value.get(portal) === ScraperConfigurationStatus.ERROR)
const lastDiscountsScrapeDate: Ref<DateTime | undefined> = computed(() => discountsLastScrapeDates.value?.[store.value.id])

const isDataOld = computed(() => lastDiscountsScrapeDate.value?.isValid && -diffFromNow(lastDiscountsScrapeDate.value.toJSDate(), 'hours') > 48)

const hasScrapedDiscounts = computed(
	() => !StoreToPortalFunctionality.get(store.value.id)?.has('pastScraper') || lastDiscountsScrapeDate.value?.isValid
)

const exportStoreData = () => {
	trackEvent('user', 'Clicked export to CSV button.', { store: props.store.displayName })

	let csvContent = 'Store Name,SKU,Product Name,Event Type,Event Name,Date From,Date To,Discount Depth\n'
	const selectedSkus = products.value
	const skus = storeSKUs
		.value(props.store.id)
		?.filter((sku) => selectedSkus.length === 0 || selectedSkus.some((selectedSku) => selectedSku.uniqueSkuId === sku.unique_sku_id))
		?.sort(sortByHumanNameComparator)

	skus?.forEach((sku) => {
		sku.events.forEach((event) => {
			let row = [
				`"${props.store.displayName}"`,
				`"${sku.unique_sku_id}"`,
				`"${sku.human_name}"`,
				`"${event.event_type}"`,
				`"${event.name}"`,
				`"${event.start_date}"`,
				`"${event.end_date}"`,
				`${event.discount_depth ?? ''}`
			]

			csvContent += row.join(',') + '\n'
		})
	})

	CSVDownload(csvContent, props.store.displayName)
}

const storeSpecificTimeZone = computed(() => {
	if (!isUsingStoreSpecificTimeZones.value || !store.value.timeZone || store.value.timeZone === 'local') {
		return undefined
	}

	const dateTime = DateTime.now().setZone(store.value.timeZone)

	return { long: dateTime.offsetNameLong ?? '', short: dateTime.offsetNameShort ?? '' }
})
</script>

<template>
	<div v-if="store" class="mb-06 flex select-none flex-col bg-level-02">
		<div class="flex items-center justify-between" @click="setContentVisibility(!isContentVisible)">
			<div class="portalContainer flex items-center gap-2">
				<PortalIcon v-if="portal" :portal="portal" :class="`portalIcon flex-center portal--${portal}`" />
				<div class="p-4">{{ store.displayName }}</div>
				<PortalFunctionality
					v-for="functionality in portalFunctionalities"
					:key="functionality"
					:portal="portal"
					:store-name="store.id"
					:type="functionality"
				/>
			</div>
			<div class="flex items-center">
				<div v-if="isUsingStoreSpecificTimeZones && storeSpecificTimeZone" class="font-caption mr-09 flex items-center text-ink-60">
					<Tooltip>
						<Icon name="earth" size="0.875" class="mr-02" />{{ storeSpecificTimeZone.short }}

						<template #content>
							<T path="storeContainer.storeSpecificTimeZoneTooltip">
								<template #storeName>{{ store.displayName }}</template>
								<template #timeZoneName>{{ storeSpecificTimeZone.long }} ({{ storeSpecificTimeZone.short }})</template>
							</T>
						</template>
					</Tooltip>
				</div>

				<div v-if="lastDiscountsScrapeDate?.isValid" class="font-caption flex items-center text-normal">
					<mdicon v-if="hasError" name="alert" class="icon--size-1 mr-1 text-message-warning" />
					Last scrape:&nbsp;
					<RelativeDateTime :date="lastDiscountsScrapeDate.toJSDate()" :class="isDataOld ? 'text-message-warning' : ''"></RelativeDateTime>
				</div>
				<Button v-if="hasError" variant="tertiary" size="s" class="ml-6" @click.stop="emitToParent('FIX_PORTAL_REDIRECT', portal)"
					>fix the scraper</Button
				>

				<mdicon :name="isContentVisible ? 'chevron-up' : 'chevron-down'" class="ml-6 mr-4" />
			</div>
		</div>

		<div
			v-if="hasScrapedDiscounts && isDataOld"
			class="flex items-center justify-center gap-03 bg-message-warning px-06 py-03 text-ink-inverted-87"
		>
			<Icon name="alert" size="1.25" />
			<template v-if="hasScrapersEnabled">
				<div class="font-paragraph-m-medium">
					<I18n path="outdatedSource.desc" :options="{ portal: portalCapitalized }" />
				</div>
				<Button v-if="isEmbedded" size="s" variant="quaternary" @click="emitToParent('FIX_PORTAL_REDIRECT', portal)"
					><I18n path="outdatedSource.openSourceConfiguration"
				/></Button>
			</template>

			<div v-else class="font-paragraph-m-medium"><I18n path="outdatedSource.descContactAdmin" :options="{ portal: portalCapitalized }" /></div>
		</div>

		<transition name="fade">
			<div
				v-if="isContentVisible"
				:class="['relative bg-level-01 p-4', { 'pointer-events-none opacity-50': isLoadingEventsWithCooldowns(store.id) }]"
			>
				<div v-if="!hasScrapedDiscounts">
					<NoStoresPlaceholder single-store @reload="emit('reload')" />
				</div>

				<div v-else-if="storeHasNoFavourites" class="flex items-center gap-03 pl-11">
					<Icon name="information-outline" size="1" class="text-message-warning" />

					<div class="font-body-s text-ink-60">
						{{ t('storeContainer.noFavouriteSKUsForStore') }}
					</div>

					<Tooltip :content="t('navigation.productsTooltip')" class="ml-05">
						<Button icon="star-outline" size="s" variant="quaternary" :disabled="isInitializing" @click="showFavouriteSKUs(store.id)">
							<div class="flex items-center gap-03">
								{{ t('storeContainer.selectProducts') }}
							</div>
						</Button>
					</Tooltip>
				</div>

				<template v-else>
					<div class="flex items-center justify-between">
						<div class="left-side flex gap-2">
							<div>
								<Button
									icon="plus"
									size="xs"
									:disabled="isEventSidebarVisible || isLoadingEventsWithCooldowns(store.id)"
									@click="addEventClicked()"
									>Add Event</Button
								>
							</div>
							<div>
								<Button variant="secondary" size="xs" :disabled="isLoadingEventsWithCooldowns(store.id)" @click="exportStoreData"
									>Export to CSV</Button
								>
							</div>

							<StoreDatesNavigation :store="store" />
						</div>

						<div class="right-side flex items-center justify-between gap-05">
							<div v-if="isLoadingStoreData(store.id)" class="flex min-w-[2rem] items-center justify-end gap-03">
								<span class="font-caption text-ink-38">
									{{ t(`storeDataLoading.${storeDataLoadingState(store.id)}`) }}
								</span>

								<Loader class="loader z-10" size="big" />
							</div>
						</div>
					</div>

					<div v-if="storeSKUs(props.store.id)?.length" class="mt-4">
						<!-- Timeline -->
						<StoreEventsTimeline :skus="storeSKUs(props.store.id)" :store="props.store" :is-data-old="isDataOld" />
					</div>
				</template>
			</div>
		</transition>
	</div>
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease-out;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
	opacity: 1;
}

.portalContainer {
	$portals: 'STEAM' 'META' 'MICROSOFT' 'PLAYSTATION' 'NINTENDO' 'HUMBLE' 'GOG' 'EPIC' 'APPLE' 'GOOGLE';

	@each $portal in $portals {
		& .portal--#{$portal} {
			background-color: var(--color-portal-#{$portal}-primary);
		}
	}

	.portalIcon {
		width: 3.5rem;
		height: 3.5rem;

		:deep(svg) {
			width: 2.75rem;
			height: 2.75rem;
			fill: theme('colors.ink.inverted.100');
		}
	}
}
</style>
